<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { RouterLink } from 'vue-router'
import { hasConsentDecision, setCookieConsent } from '@/utils/cookieConsent'

const isVisible = ref(!hasConsentDecision())

onMounted(() => {
  if (!hasConsentDecision()) {
    isVisible.value = true
  }
})

const accept = () => {
  setCookieConsent('accepted')
  isVisible.value = false
}

const decline = () => {
  setCookieConsent('declined')
  isVisible.value = false
}
</script>

<template>
  <Transition name="cookie-banner-fade">
    <aside
      v-if="isVisible"
      id="cookie-consent-banner"
      class="cookie-banner"
      role="region"
      aria-label="Уведомление об использовании файлов cookie"
    >
      <div class="cookie-banner__inner">
        <div class="cookie-banner__content">
          <p class="cookie-banner__text">
            Мы используем файлы cookie и сервисы аналитики для корректной работы сайта и улучшения
            пользовательского опыта. Нажимая «Принять», вы даёте согласие на обработку данных в
            соответствии с
            <RouterLink to="/policy" class="cookie-banner__link"
              >Политикой конфиденциальности</RouterLink
            >
            и
            <RouterLink to="/terms" class="cookie-banner__link"
              >Пользовательским соглашением</RouterLink
            >.
          </p>
        </div>
        <div class="cookie-banner__actions">
          <button
            type="button"
            class="cookie-banner__btn cookie-banner__btn--secondary"
            aria-label="Отклонить использование аналитических cookie"
            @click="decline"
          >
            Отклонить
          </button>
          <button
            type="button"
            class="cookie-banner__btn cookie-banner__btn--primary"
            aria-label="Принять использование всех файлов cookie"
            @click="accept"
          >
            Принять
          </button>
        </div>
      </div>
    </aside>
  </Transition>
</template>

<style scoped>
.cookie-banner {
  position: fixed;
  bottom: 1.5rem;
  left: 1.5rem;
  right: 1.5rem;
  max-width: 68rem;
  margin: 0 auto;
  z-index: 9999;
  background-color: var(--color-bg-primary);
  border: 1px solid var(--color-gray-200);
  box-shadow: 0 0.5rem 1.875rem rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
}

.cookie-banner__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  padding: 1.25rem 1.75rem;
}

.cookie-banner__content {
  flex: 1;
}

.cookie-banner__text {
  font-family: Montserrat, Arial, sans-serif;
  font-size: 0.813rem;
  line-height: 1.45;
  font-weight: 400;
  color: var(--color-text-secondary);
  margin: 0;
}

.cookie-banner__link {
  color: var(--color-primary);
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: opacity 0.2s ease;
}

.cookie-banner__link:hover {
  opacity: 0.7;
}

.cookie-banner__actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}

.cookie-banner__btn {
  font-family: Montserrat, Arial, sans-serif;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.06rem;
  padding: 0.625rem 1.25rem;
  height: 2.5rem;
  cursor: pointer;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition:
    background-color 0.25s ease,
    color 0.25s ease,
    border-color 0.25s ease;
}

.cookie-banner__btn--primary {
  background-color: var(--color-primary);
  color: var(--color-white);
  border: 1px solid var(--color-primary);
}

.cookie-banner__btn--primary:hover {
  background-color: var(--color-bg-darker);
}

.cookie-banner__btn--secondary {
  background-color: transparent;
  color: var(--color-text-secondary);
  border: 1px solid var(--color-gray-500);
}

.cookie-banner__btn--secondary:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

/* Поддержка тёмной темы сайта */
:global(html.dark-theme) .cookie-banner,
:global(body.dark-theme) .cookie-banner {
  background-color: var(--color-bg-dark);
  border-color: rgba(255, 255, 255, 0.15);
  box-shadow: 0 0.5rem 1.875rem rgba(0, 0, 0, 0.4);
}

:global(html.dark-theme) .cookie-banner__text,
:global(body.dark-theme) .cookie-banner__text {
  color: var(--color-white);
}

:global(html.dark-theme) .cookie-banner__link,
:global(body.dark-theme) .cookie-banner__link {
  color: var(--color-white);
}

:global(html.dark-theme) .cookie-banner__btn--primary,
:global(body.dark-theme) .cookie-banner__btn--primary {
  background-color: var(--color-white);
  color: var(--color-primary);
  border-color: var(--color-white);
}

:global(html.dark-theme) .cookie-banner__btn--primary:hover,
:global(body.dark-theme) .cookie-banner__btn--primary:hover {
  background-color: var(--color-gray-200);
}

:global(html.dark-theme) .cookie-banner__btn--secondary,
:global(body.dark-theme) .cookie-banner__btn--secondary {
  background-color: transparent;
  color: var(--color-white);
  border-color: rgba(255, 255, 255, 0.3);
}

:global(html.dark-theme) .cookie-banner__btn--secondary:hover,
:global(body.dark-theme) .cookie-banner__btn--secondary:hover {
  border-color: var(--color-white);
}

/* Анимация появления/скрытия */
.cookie-banner-fade-enter-active,
.cookie-banner-fade-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.cookie-banner-fade-enter-from,
.cookie-banner-fade-leave-to {
  opacity: 0;
  transform: translateY(1.5rem);
}

/* Адаптив для планшетов и мобильных */
@media (max-width: 48rem) {
  .cookie-banner {
    bottom: 1rem;
    left: 1rem;
    right: 1rem;
  }

  .cookie-banner__inner {
    flex-direction: column;
    align-items: stretch;
    gap: 1.25rem;
    padding: 1.25rem;
  }

  .cookie-banner__actions {
    flex-direction: column-reverse;
    width: 100%;
  }

  .cookie-banner__btn {
    width: 100%;
    height: 2.75rem;
  }
}
</style>
